import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Share2, Eye, Download, IdCard } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { api } from '../lib/api';
import { viewUrl, downloadUrl } from '../lib/files';
import { KeyValueRow, Eyebrow } from '../components/ui';
import { ShareSheet } from '../components/ShareSheet';
import { useCopy, useToast } from '../components/Toast';
import { DOC_LABEL, dateIT, fieldLabel } from '../lib/format';
import type { DocumentDTO } from '@shared/types';

export function DettaglioDocumentoScreen() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { data, loading } = useAsync(() => store.document(id), [id]);
  const copy = useCopy();
  const { show } = useToast();
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [share, setShare] = useState(false);

  if (loading) return <div className="screen">Caricamento…</div>;
  if (!data) return <div className="screen">Documento non trovato.</div>;

  const rows = buildRows(data);

  const copyAll = () => copy(rows.map((r) => `${r.label}: ${r.value}`).join('\n'), 'Copiati tutti i dati');

  // Preferisci il PDF, poi il fronte (per Visualizza/Scarica/Condividi del file).
  const which: 'pdf' | 'front' | null = data.has_pdf ? 'pdf' : data.has_front ? 'front' : null;

  const onView = async () => {
    if (!which) return show('Nessun file allegato');
    try {
      const { url } = await api.documentFileUrl(data.id, which, 'inline');
      viewUrl(url);
    } catch {
      show('File non disponibile');
    }
  };

  const onDownload = async () => {
    if (!which) return show('Nessun file da scaricare');
    try {
      const { url } = await api.documentFileUrl(data.id, which, 'attachment');
      downloadUrl(url, `${data.title}-${data.holder}.${which === 'pdf' ? 'pdf' : 'jpg'}`);
    } catch {
      show('Download non disponibile');
    }
  };

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <div className="topbar__title">{data.title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{data.holder}</div>
        </div>
        <button className="iconbtn" onClick={() => setShare(true)} aria-label="Condividi">
          <Share2 size={18} />
        </button>
      </div>

      {/* Anteprima formato tessera */}
      <div className="docpreview">
        <IdCard size={40} />
        <span className="eyebrow">{side === 'front' ? 'FRONTE' : 'RETRO'}</span>
      </div>
      <div className="dots">
        <button className={`dot${side === 'front' ? ' is-active' : ''}`} onClick={() => setSide('front')} aria-label="Fronte" />
        {data.has_back && (
          <button className={`dot${side === 'back' ? ' is-active' : ''}`} onClick={() => setSide('back')} aria-label="Retro" />
        )}
      </div>

      {/* Tre azioni */}
      <div className="actions3" style={{ marginTop: 18 }}>
        <button className="btn" onClick={onView}>
          <Eye size={18} /> Visualizza
        </button>
        <button className="btn btn--accent" onClick={onDownload}>
          <Download size={18} /> Scarica
        </button>
        <button className="btn" onClick={() => setShare(true)}>
          <Share2 size={18} /> Condividi
        </button>
      </div>

      {/* Dati testuali */}
      <div className="section-head">
        <Eyebrow>Dati testuali</Eyebrow>
        <button className="section-head__link" onClick={copyAll}>
          Copia tutto
        </button>
      </div>
      <div className="card" style={{ padding: '4px 16px' }}>
        {rows.map((r) => (
          <KeyValueRow key={r.label} label={r.label} value={r.value} mono={r.mono} />
        ))}
      </div>

      {share && (
        <ShareSheet
          resourceType="document"
          resourceId={data.id}
          name={`${data.title} · ${data.holder}`}
          onClose={() => setShare(false)}
        />
      )}
    </div>
  );
}

function buildRows(d: DocumentDTO): { label: string; value: string; mono?: boolean }[] {
  const rows: { label: string; value: string; mono?: boolean }[] = [];
  if (d.number) rows.push({ label: 'Numero', value: d.number, mono: true });
  rows.push({ label: 'Cognome e nome', value: d.holder });
  rows.push({ label: 'Tipo', value: DOC_LABEL[d.type] });
  if (d.expires_at) rows.push({ label: 'Scadenza', value: dateIT(d.expires_at), mono: true });
  if (d.issued_by) rows.push({ label: 'Comune di rilascio', value: d.issued_by });
  for (const [k, v] of Object.entries(d.fields)) {
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(String(v));
    rows.push({ label: fieldLabel(k), value: isDate ? dateIT(String(v)) : String(v), mono: isDate });
  }
  return rows;
}
